import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import momentTz from 'moment-timezone';

import { getNextNumericalName } from '@avtechsoftware/ra-javascript-utilities/src/utilities/validation/ObjectListUtils';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import {
  createQuietTime,
  fetchQuietTimes,
  selectQuietTimes,
  selectQuietTimesCrudOperation,
  selectQuietTimesCrudStatus,
  setQuietTimeCrudOperation,
} from '../../../../redux/slices/quietTimesSlice/quietTimesSlice';
import { pushSessionMessage } from '../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import { CrudType } from '../../../../utility/types/CrudType';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import { defaultTimePeriod } from '../../../hooks/useQuietTimes/useQuietTimes';
import QuietTimeForm from '../../../alert_actions/quiet_times/QuietTimeFormLayout/QuietTimeForm/QuietTimeForm';
import { selectActionsData, updateStepData } from '../../../../redux/slices/alertFormSlice/alertFormSlice';

function NewQuietTimeModal(props) {
  const { show, handleClose, actionIdToApplySchedule = null } = props;

  const dispatch = useDispatch();
  const crudOperation = useSelector(selectQuietTimesCrudOperation);
  const crudStatus = useSelector(selectQuietTimesCrudStatus);
  const quietTimes = useSelector(selectQuietTimes);
  const { actions } = useSelector(selectActionsData);
  const { timezone } = useSelector(selectSessionInformation);

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [submitBtnText, setSubmitBtnText] = useState('Save');

  const handleSuccess = (message) => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_SUCCESS,
      title: 'Saved',
      body: message,
    }));
    dispatch(setQuietTimeCrudOperation(CrudType.READ));
    setIsFormSubmitting(false);
    setSubmitBtnText('Save');
    handleClose();
  };

  const handleFailure = (title, message) => {
    dispatch(fetchQuietTimes());
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_ERROR,
      title,
      body: message,
    }));
    setIsFormSubmitting(false);
    setSubmitBtnText('Save');
  };

  useEffect(() => {
    if (crudOperation === CrudType.CREATE) {
      if (crudStatus === CrudStatus.SUCCESS) {
        handleSuccess('Quiet Time saved successfully.');
      } else if (crudStatus === CrudStatus.ERROR) {
        handleFailure('Save Error', 'Error saving Quiet Time.');
      }
    }
  }, [crudOperation, crudStatus]);

  const [quietTime, setQuietTime] = useState({
    name: '',
    timezone: '',
    quietTimeScheduleTimePeriodList: [],
  });

  useEffect(() => {
    if (show) {
      setQuietTime({
        name: getNextNumericalName(quietTimes, 'New Quiet Time', 'name'),
        timezone: timezone || momentTz.tz.guess(),
        quietTimeScheduleTimePeriodList: [defaultTimePeriod],
      });
      setIsFormSubmitting(false);
      setSubmitBtnText('Save');
    }
  }, [show]);

  const updateQuietTime = (field, value) => {
    setQuietTime((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onNameChange = (e) => {
    updateQuietTime('name', e.target.value);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsFormSubmitting(true);
    setSubmitBtnText('Saving...');
    await dispatch(createQuietTime(quietTime)).unwrap()
      .then((res) => {
        if (actionIdToApplySchedule) {
          const updatedActions = actions.map((action) => {
            if (action.id !== actionIdToApplySchedule) return action;
            return {
              ...action,
              quietTimeId: res.id,
            };
          });
          dispatch(updateStepData({ step: 'DEFINE_ACTIONS', data: { actions: updatedActions } }));
        }
      });
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>New Quiet Time</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="container-fluid">
          <div className="row mb-4">
            <div className="col-12">
              <input
                type="text"
                aria-label="quiet-time template name"
                className="form-control"
                placeholder="New Quiet Time"
                value={quietTime.name}
                onChange={onNameChange}
              />
            </div>
          </div>
        </div>

        <QuietTimeForm
          quietTime={quietTime}
          updateQuietTime={updateQuietTime}
        />
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isFormSubmitting}
        >
          {submitBtnText}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

NewQuietTimeModal.defaultProps = {
  actionIdToApplySchedule: null,
};

NewQuietTimeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  actionIdToApplySchedule: PropTypes.number,
};

export default NewQuietTimeModal;
